import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Link as RouterLink, useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import AddIcon from 'components/material-symbol/icons/Add'
import CampaignOutlinedIcon from 'components/material-symbol/icons/Campaign'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import SendIcon from 'components/material-symbol/icons/Send'
import Error from 'components/error'
import { TonalButton } from 'components/empty-page-state'
import { PageHeader } from 'components/page-header'
import SectionEmptyState from 'components/section-empty-state'
import { useAuth } from 'lib/auth'
import { useCoachCampaigns } from './api-hooks'
import { StatusBadge } from './badges'
import DeleteDraftDialog from './delete-draft-dialog'
import { DEFAULT_ATTRIBUTION_WINDOW_DAYS, STEP_PARAM } from './constants'
import { formatCampaignDate, pluralize } from './format'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import useStyles from './styles'
import SectionTitle from 'components/section-title'

const campaignPath = (basePath, campaign) =>
  campaign.status === 'DRAFT'
    ? `${basePath}/${campaign.id}/edit`
    : `${basePath}/${campaign.id}`

const DeleteDraftButton = ({ campaign }) => {
  const classes = useStyles()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  return (
    <>
      <Tooltip title="Delete draft">
        <IconButton
          aria-label="Delete draft"
          data-testid={`campaign-delete-${campaign.id}`}
          className={classes.borderedActionButton}
          onClick={() => setIsDeleteOpen(true)}
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isDeleteOpen ? (
        <DeleteDraftDialog
          campaign={campaign}
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
        />
      ) : null}
    </>
  )
}

DeleteDraftButton.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
}

export function CampaignListView({ campaigns, basePath }) {
  const classes = useStyles()
  const settingsClasses = useSettingsStyles()
  const attributionWindowDays =
    campaigns.find(campaign => campaign.results?.attributionWindowDays)?.results
      .attributionWindowDays || DEFAULT_ATTRIBUTION_WINDOW_DAYS
  const newCampaignButton = (
    <Button
      variant="contained"
      color="primary"
      className={classes.pillButton}
      component={RouterLink}
      to={`${basePath}/new`}
      startIcon={<AddIcon />}
    >
      New Campaign
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Campaigns"
        actions={newCampaignButton}
        fixedMobileActions
        keepActionsInline
      />
      <Box className={classes.pageContent}>
        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <SectionTitle>Your campaigns</SectionTitle>
            <Typography className={classes.cardSubtitle}>
              Each student gets the message in their chat with you. Bookings are
              counted for {attributionWindowDays} days after you send.
            </Typography>
          </Box>
          {campaigns.length === 0 ? (
            <SectionEmptyState
              Icon={CampaignOutlinedIcon}
              title="No campaigns yet"
              caption="Tell students about open lesson times, clinics, or programs, and see who books."
              bordered={false}
              action={
                <TonalButton to={`${basePath}/new`} startIcon={<AddIcon />}>
                  New Campaign
                </TonalButton>
              }
            />
          ) : (
            <Box>
              {campaigns.map(campaign => (
                <Box key={campaign.id} className={classes.row}>
                  <Box
                    component={RouterLink}
                    to={campaignPath(basePath, campaign)}
                    className={classes.rowLink}
                    data-testid={`campaign-${campaign.id}`}
                  >
                    <Box className={settingsClasses.rowIcon}>
                      <CampaignOutlinedIcon />
                    </Box>
                    <Box className={classes.rowBody}>
                      <Typography className={classes.rowTitle}>
                        {campaign.name}
                      </Typography>
                      <Typography className={classes.rowDetail}>
                        {formatCampaignDate(campaign)}
                        {campaign.recipientCount > 0
                          ? ` · ${pluralize(
                            campaign.recipientCount,
                            'student',
                          )}`
                          : ''}
                      </Typography>
                    </Box>
                    {campaign.status === 'SENT' ? (
                      <Box className={classes.rowStats}>
                        <Box className={classes.rowStat}>
                          <Typography
                            className={clsx(
                              classes.rowStatValue,
                              campaign.results.directBookingCount > 0
                                ? classes.rowStatValueSuccess
                                : classes.rowStatValueMuted,
                            )}
                          >
                            {campaign.results.directBookingCount}
                          </Typography>
                          <Typography className={classes.rowStatLabel}>
                            From link
                          </Typography>
                        </Box>
                        <Box className={classes.rowStat}>
                          <Typography className={classes.rowStatValue}>
                            {campaign.results.influencedBookingCount}
                          </Typography>
                          <Typography className={classes.rowStatLabel}>
                            Booked later
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                  {campaign.status === 'DRAFT' ? (
                    <Box className={classes.rowActions}>
                      <DeleteDraftButton campaign={campaign} />
                      <Tooltip title="Review and send">
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          aria-label="Review and send"
                          className={classes.sendActionButton}
                          component={RouterLink}
                          to={`${basePath}/${campaign.id}/edit?${STEP_PARAM}=review`}
                          data-testid={`campaign-review-${campaign.id}`}
                        >
                          <SendIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </Box>
                  ) : null}
                  <Box className={classes.rowStatus}>
                    <StatusBadge status={campaign.status} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

CampaignListView.propTypes = {
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      recipientCount: PropTypes.number.isRequired,
      results: PropTypes.shape({
        directBookingCount: PropTypes.number.isRequired,
        influencedBookingCount: PropTypes.number.isRequired,
        attributionWindowDays: PropTypes.number,
      }).isRequired,
    }),
  ).isRequired,
  basePath: PropTypes.string.isRequired,
}

const CampaignList = () => {
  const { url } = useRouteMatch()
  const { user } = useAuth()
  const hasPaidTier = user?.coach?.hasPaidTierSubscription
  const { data, loading, error } = useCoachCampaigns({ skip: !hasPaidTier })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  return (
    <CampaignListView
      campaigns={data?.coachCampaigns || []}
      basePath={url.replace(/\/$/, '')}
    />
  )
}

export default CampaignList
